import React from 'react';
import { connect } from 'react-redux';
import { fetchEventDetails } from '../../actions/event';
import { fetchResponses } from '../../actions/responses';
import './Segmentation.css';
import Event from '../../models/Event';
import ErrorAndRefresh from '../shared/ErrorAndRefresh';
import { handleError } from '../../util/Errors';
import { arrowDownIcon, arrowUpIcon } from '../../constants';
import Dropdown from '../shared/Dropdown';
import Segments from '../../models/Segments';
import EmptyState from '../shared/EmptyState';
import SegmentValues from '../../models/SegmentValues';
import { SegmentationChart } from '../charts/SegmentationChart';
import Logger from '../../util/Logger';
import _ from 'lodash';
import { CreatorReportAnalytics } from '../../analytics/CreatorReportAnalytics';

const Sorts = {
  SEGMENT: 'segment',
  AVERAGE: 'average'
};

class Segmentation extends React.Component {

  constructor (props) {
    super(props);

    this.state = {
      activeSort: Sorts.SEGMENT,
      segmentSortReversed: false,
      averageSortReversed: false,
      selectedDropdownItem: this.getDropdownItemList()[0]
    };
  }

  loadStats = () => {
    const { fetchEventDetails, fetchResponses, event } = this.props;
    fetchEventDetails(Event.getId(event));
    fetchResponses(Event.getId(event));
  };

  handleEditClicked = ($event) => {
    $event.preventDefault();

    CreatorReportAnalytics.segmentEditClicked();
    const { onEditClicked } = this.props;
    onEditClicked($event);
  };

  handleSortByRegion = ($event) => {
    const columnName = this.state.selectedDropdownItem.text;
    CreatorReportAnalytics.segmentSort(columnName);
    $event.preventDefault();
    this.setState({
      segmentSortReversed: !this.state.segmentSortReversed,
      activeSort: Sorts.SEGMENT
    });
  };

  handleSortByAverage = ($event) => {
    CreatorReportAnalytics.segmentSortByAverage();
    $event.preventDefault();
    this.setState({
      averageSortReversed: !this.state.averageSortReversed,
      activeSort: Sorts.AVERAGE
    });
  };

  onDropdownSelected = (item) => {
    CreatorReportAnalytics.segmentDropdownSelected(item.text);
    this.setState({ selectedDropdownItem: item });
  };

  getDropdownItemList = () => {
    const { segmentation } = this.props;

    if (!segmentation || segmentation.length === 0) {
      return [];
    }

    return segmentation.map(s => Segments.getName(s)).map(r => ({
      id: r,
      text: r
    }));
  };

  componentDidUpdate (prevProps) {
    const { pulseStatsError, segmentation } = this.props;
    handleError(pulseStatsError, prevProps.pulseStatsError);

    const hasSegmentation = (!!segmentation && segmentation.length > 0);

    // Set the segmentation active dropdown item once we have a list
    if (hasSegmentation &&
      (!this.state.selectedDropdownItem || !this.state.selectedDropdownItem.id)) {
      this.setState({ selectedDropdownItem: this.getDropdownItemList()[0] });
    }

    // If there is no segment, the roster may have changed
    const currentSegment = this.getSegment(segmentation);
    if (!currentSegment && currentSegment !== this.getSegment(prevProps.segmentation)) {
      this.setState({ selectedDropdownItem: this.getDropdownItemList()[0] });
    }
  }

  sortByActive = (segment) => {
    const { activeSort, averageSortReversed, segmentSortReversed } = this.state;
    // TODO move into a Sorts file
    if (activeSort === Sorts.AVERAGE) {
      const result = _.sortBy(segment, s => SegmentValues.getSimpleScore(s));
      return averageSortReversed ? _.reverse(result) : result;
    } else if (activeSort === Sorts.SEGMENT) {
      const result = _.sortBy(segment, s => SegmentValues.getName(s));
      return segmentSortReversed ? _.reverse(result) : result;
    } else {
      Logger.assert('Invalid sort: ', activeSort);
    }
  };

  getSegment = (segmentation) => {
    const hasSegmentation = (!!segmentation && segmentation.length > 0);
    if (!hasSegmentation) {
      return [];
    }

    const { selectedDropdownItem } = this.state;
    if (!selectedDropdownItem) {
      return [];
    }

    return segmentation.filter(s => Segments.getName(s) === selectedDropdownItem.text)[0];
  };

  render () {
    const { activeSort, selectedDropdownItem } = this.state;
    const { editing, segmentation, segmentationError, isPdfRender } = this.props;

    const hasSegmentation = (!!segmentation && segmentation.length > 0);
    if (segmentationError) {
      return (
        <div className="segmentation">
          <ErrorAndRefresh
            error="Failed to load Segmentation"
            onRefresh={ this.loadStats }
          />
        </div>
      );
    }

    if (!hasSegmentation || !selectedDropdownItem || !selectedDropdownItem.id ||
      !selectedDropdownItem.text) {
      return (
        <div className="segmentation">
          <EmptyState
            title="No Segmentation"
            message="Segmentation will appear here when it exists." />
        </div>
      );
    }

    const isRegionSortActive = (activeSort === Sorts.SEGMENT);
    const isAverageSortActive = (activeSort === Sorts.AVERAGE);

    const chartSection = (activeSegment) => {
      if (!activeSegment) {
        Logger.warn('No activeSegment, chart may have changed roster data');
        return null;
      }

      let hasMultipleSegment = false;
      for (let s of (Segments.getValues(activeSegment) || [])) {
        if (SegmentValues.getName(s) === 'Multiple') {
          hasMultipleSegment = true;
        }
      }

      return (
        <div key={ Segments.getName(activeSegment) } className="row no-gutters segmentation-print">
          <div className="col">
            <div className="row no-gutters segmentation-list-header segmentation-list-item">
              <div className="region text-truncate font-weight-semibold font-size-md">
                    <span className="clickable" onClick={ this.handleSortByRegion }>
                      { Segments.getName(activeSegment) }
                    </span>
                <i
                  onClick={ this.handleSortByRegion }
                  className={ `material-icons clickable d-print-none ${isRegionSortActive
                    ? 'enabled-icon'
                    : 'disabled-icon'}` }>
                  { this.state.segmentSortReversed ? arrowUpIcon : arrowDownIcon }
                </i>
              </div>
              <div className="average text-truncate font-weight-semibold font-size-md">
                      <span
                        className="clickable"
                        onClick={ this.handleSortByAverage }>Average</span>
                <i
                  onClick={ this.handleSortByAverage }
                  className={ `material-icons clickable d-print-none ${isAverageSortActive
                    ? 'enabled-icon'
                    : 'disabled-icon'}` }>
                  { this.state.averageSortReversed ? arrowUpIcon : arrowDownIcon }
                </i>
              </div>
              <div className="sentiment text-truncate font-weight-semibold font-size-md">
                Sentiment
              </div>
            </div>

            <div className="segmentation-list-container">
              <div className="col segmentation-list">
                { this.sortByActive(Segments.getValues(activeSegment)).map(
                  s => <SegmentationListItem
                    key={ SegmentValues.getName(s) }
                    name={ Segments.getName(activeSegment) }
                    segment={ s }
                    hasMultipleSegment={ hasMultipleSegment }
                    isPdfRender={ isPdfRender }
                  />
                ) }
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="segmentation print-card-header">
        <div className="row no-gutters">
          <div className="col margin-if-print">
            <div className="row no-gutters p-3 d-print-none">
              <div className="my-auto">
                <div className="segmentation-dropdown">
                  <Dropdown
                    id="segmentation-dropdown-id"
                    text={ selectedDropdownItem.text }
                    itemList={ this.getDropdownItemList() }
                    onSelected={ this.onDropdownSelected }
                  />
                </div>
              </div>
              <div className="fix-ie" />
              { editing && (
                <button
                  className="btn pop-btn my-auto"
                  onClick={ this.handleEditClicked }>
                  Edit
                </button>
              ) }
            </div>

            { ((!!isPdfRender || (!!window.matchMedia && window.matchMedia('print').matches)) && hasSegmentation)
              ? segmentation.filter(s => !!s).map(s => chartSection(s))
              : chartSection(this.getSegment(segmentation))
            }

          </div>
        </div>
      </div>
    );
  }

}

const SegmentationListItem = ({ name, segment, hasMultipleSegment, isPdfRender }) => {
  const display = SegmentValues.getSimpleScoreDisplay(segment);

  // Data layout is reverse of the visual layout which is positive, neutral, negative.
  const data = [
    {
      // Negative Sentiments
      data: [
        {
          y: parseFloat(SegmentValues.getSentimentNegativePercent(segment).toFixed(2)),
          className: 'page-break-avoid'
        }
      ],
      pointStart: 1,
      color: '#FD7D2B'
    },
    {
      // Neutral Sentiments
      data: [
        {
          y: parseFloat(SegmentValues.getSentimentNeutralPercent(segment).toFixed(2)),
          className: 'page-break-avoid'
        }
      ],
      pointStart: 1,
      color: '#EEEEEE'
    },
    {
      // Positive Sentiments
      data: [
        {
          y: parseFloat(SegmentValues.getSentimentPositivePercent(segment).toFixed(2)),
          className: 'page-break-avoid'
        }
      ],
      pointStart: 1,
      color: '#3D9AE2'
    }
  ];

  return (
    <div className="row no-gutters">
      <div className="col">
        { SegmentValues.isInsufficientActivity(segment)
          ? (
            <div className="row segmentation-list-item segmentation-list-item-real">
              <div className="region my-auto">
                { SegmentValues.getName(segment) }
              </div>
              <div className="font-size-sm my-auto">
                Insufficient activity within subgroup. { !!hasMultipleSegment
                ? `Please reference 'Multiple' category`
                : '' }
              </div>
            </div>
          )
          : (
            <div className="row segmentation-list-item segmentation-list-item-real">
              <div className="region my-auto">
                { SegmentValues.getName(segment) }
              </div>
              <div className="average my-auto">
                { display === null ? 'N/A' : display }
              </div>
              <div className="sentiment my-auto">
                <SegmentationChart
                  chartId={ `<segmentation-${generateUUID()}` }
                  data={ data }
                  customChartContainerClass="segmentation-chart-container"
                  customChartClass="segmentation-chart"
                  forceReflowBecauseOfBug={ true }
                  disableAnimation={ isPdfRender }
                />
              </div>
            </div>
          )
        }
      </div>
    </div>
  );
};

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

const mapStateToProps = state => {
  const user = state.user.user;
  return {
    user: user,
    segmentationError: state.event.segmentationError
  };
};

export default connect(mapStateToProps, {
  fetchEventDetails,
  fetchResponses
})(Segmentation);
